"use client";

import { datadogRum } from "@datadog/browser-rum";

/**
 * Client-side RUM events for Datadog tracking
 */

export interface BotDetectionRumEvent {
  fraudRiskScore: number;
  fraudIndicators: string[];
  isBotDetected: boolean;
  assessmentData: {
    vpn?: boolean;
    proxied?: boolean;
    rdp?: boolean;
    dch?: boolean;
    anon?: boolean;
    complete?: boolean;
    sid?: string;
  };
  context?: {
    userId?: string;
    email?: string;
    ipAddress?: string;
    country?: string;
  };
}

/**
 * Track bot detection event in Datadog RUM
 * @param eventData - Bot detection event data
 */
export function trackBotDetectionRumEvent(
  eventData: BotDetectionRumEvent,
): void {
  try {
    if (!datadogRum.getInternalContext()) {
      console.warn(
        "[Datadog RUM] Not initialized, skipping bot detection event",
      );
      return;
    }

    // Add custom action for bot detection
    datadogRum.addAction("bot_detection", {
      fraudRiskScore: eventData.fraudRiskScore,
      fraudIndicators: eventData.fraudIndicators.join(","),
      isBotDetected: eventData.isBotDetected,
      assessmentFlags: Object.entries(eventData.assessmentData)
        .filter(([_, value]) => typeof value === "boolean" && value)
        .map(([key, _]) => key)
        .join(","),
      ...eventData.context,
    });

    // Add custom error if bot is detected
    if (eventData.isBotDetected) {
      datadogRum.addError("Bot detected by Monocle", {
        fraudRiskScore: eventData.fraudRiskScore,
        fraudIndicators: eventData.fraudIndicators,
        assessmentData: eventData.assessmentData,
        ...eventData.context,
      });
    }

    console.log("[Datadog RUM] Bot detection event tracked:", {
      isBotDetected: eventData.isBotDetected,
      fraudRiskScore: eventData.fraudRiskScore,
      fraudIndicators: eventData.fraudIndicators,
    });
  } catch (error) {
    console.error("[Datadog RUM] Failed to track bot detection event:", error);
  }
}

/**
 * Track custom RUM event
 * @param eventName - Name of the event
 * @param eventData - Event data
 */
export function trackCustomRumEvent(
  eventName: string,
  eventData: Record<string, unknown>,
): void {
  try {
    if (!datadogRum.getInternalContext()) {
      console.warn("[Datadog RUM] Not initialized, skipping custom event");
      return;
    }

    datadogRum.addAction(eventName, eventData);

    console.log("[Datadog RUM] Custom event tracked:", {
      eventName,
      eventData,
    });
  } catch (error) {
    console.error("[Datadog RUM] Failed to track custom event:", error);
  }
}

/**
 * Track RUM error
 * @param error - Error object or message
 * @param context - Additional context
 */
export function trackRumError(
  error: Error | string,
  context?: Record<string, unknown>,
): void {
  try {
    if (!datadogRum.getInternalContext()) {
      console.warn("[Datadog RUM] Not initialized, skipping error tracking");
      return;
    }

    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;

    datadogRum.addError(errorMessage, {
      stack: errorStack,
      ...context,
    });

    console.log("[Datadog RUM] Error tracked:", { errorMessage, context });
  } catch (err) {
    console.error("[Datadog RUM] Failed to track error:", err);
  }
}
